import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import { CollapsibleSection } from './collapsibleSection'
import {
    BASE_URL,
    LenderClientRow,
    LenderPurgeExportResponse,
    LenderPurgePreview,
    LenderPurgeResult,
    LenderSummaryResponse,
    buildLenderPurgeExportCsv,
    downloadCsvFile,
} from './shared'

const SUMMARY_PATH = '/api/fairclaim-clients-by-lender'
const PURGE_PATH = '/api/fairclaim-clients-purge-by-lender'
const EXPORT_PATH = '/api/fairclaim-clients-purge-by-lender-export'

interface Props {
    /** Bump from the parent to reload the lender table (shared with stats/list refresh). */
    refreshNonce: number
    /** Called after a successful purge so the parent can reload stats and the delivery list. */
    onPurged: () => void
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

/**
 * "Clients by lender" — per-lender client counts split by funding-batch
 * membership, plus a preview/confirm purge of the never-batched clients for
 * the selected lenders. Rendered as the body of a collapsible subsection in
 * the optics data-management group; it fetches when first expanded.
 */
export const LenderPurgeCard = ({ refreshNonce, onPurged }: Props) => {
    const [rows, setRows] = useState<LenderClientRow[]>([])
    const [totals, setTotals] = useState<LenderSummaryResponse['totals'] | null>(null)
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [localNonce, setLocalNonce] = useState(0)
    const [selected, setSelected] = useState<Set<string>>(() => new Set())
    const [preview, setPreview] = useState<LenderPurgePreview | null>(null)
    const [previewLoading, setPreviewLoading] = useState(false)
    const [purging, setPurging] = useState(false)
    const [confirmText, setConfirmText] = useState('')
    const [result, setResult] = useState<LenderPurgeResult | null>(null)
    const [hideNonPurgeable, setHideNonPurgeable] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [exportNote, setExportNote] = useState<string | null>(null)

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            setLoading(true)
            setLoadError(null)
            try {
                const response = await api.post(`${BASE_URL}${SUMMARY_PATH}`, {})
                if (cancelled) return
                if (!response.data?.success) throw new Error(response.data?.message || 'Request failed')
                const data = response.data as LenderSummaryResponse
                setRows(data.lenders ?? [])
                setTotals(data.totals ?? null)
                // Drop selections for lenders that no longer exist / have nothing purgeable / lost their defendant.
                setSelected((current) => {
                    const purgeable = new Set(
                        (data.lenders ?? []).filter((l) => !l.unmatched && l.notInBatch > 0).map((l) => l.lender),
                    )
                    const next = new Set(Array.from(current).filter((l) => purgeable.has(l)))
                    return next.size === current.size ? current : next
                })
            } catch (error: any) {
                if (!cancelled) {
                    setLoadError(error?.response?.data?.message || error?.message || 'Could not load clients by lender')
                    setRows([])
                    setTotals(null)
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }
        run()
        return () => {
            cancelled = true
        }
    }, [refreshNonce, localNonce])

    // Only configured defendants are purgeable from this table; spellings with no
    // matching defendant are listed separately below for reference.
    const matchedRows = useMemo(() => rows.filter((r) => !r.unmatched), [rows])
    const unmatchedRows = useMemo(() => rows.filter((r) => r.unmatched), [rows])
    const unmatchedNotInBatch = useMemo(() => unmatchedRows.reduce((n, r) => n + r.notInBatch, 0), [unmatchedRows])
    const purgeableRows = useMemo(() => matchedRows.filter((r) => r.notInBatch > 0), [matchedRows])
    // Display-only filter; selection and totals always work off the full list.
    const visibleRows = hideNonPurgeable ? purgeableRows : matchedRows
    const hiddenCount = matchedRows.length - visibleRows.length
    const selectedList = useMemo(() => Array.from(selected).sort((a, b) => a.localeCompare(b)), [selected])
    const selectedNotInBatch = useMemo(
        () => rows.filter((r) => selected.has(r.lender)).reduce((n, r) => n + r.notInBatch, 0),
        [rows, selected],
    )
    const allPurgeableSelected = purgeableRows.length > 0 && purgeableRows.every((r) => selected.has(r.lender))

    const resetPurgeFlow = useCallback(() => {
        setPreview(null)
        setConfirmText('')
        setExportNote(null)
    }, [])

    const toggleLender = (lender: string) => {
        setSelected((current) => {
            const next = new Set(current)
            if (next.has(lender)) next.delete(lender)
            else next.add(lender)
            return next
        })
        resetPurgeFlow()
        setResult(null)
    }

    const toggleAll = () => {
        setSelected(allPurgeableSelected ? new Set() : new Set(purgeableRows.map((r) => r.lender)))
        resetPurgeFlow()
        setResult(null)
    }

    const runPreview = async () => {
        if (!selectedList.length) return
        setPreviewLoading(true)
        setResult(null)
        try {
            const response = await api.post(`${BASE_URL}${PURGE_PATH}`, {
                lenders: selectedList,
                preview: true,
            })
            if (!response.data?.success) throw new Error(response.data?.message || 'Preview failed')
            setPreview(response.data as LenderPurgePreview)
            setConfirmText('')
            setExportNote(null)
        } catch (error: any) {
            Report.failure(
                'Preview failed',
                error?.response?.data?.message || error?.message || 'Could not preview the lender purge.',
                'Okay',
            )
        } finally {
            setPreviewLoading(false)
        }
    }

    /**
     * Download every client + claim reference the purge would delete (the full
     * set, not the 200-client preview sample) so it can be sent to the external
     * claim provider. Read-only on the server.
     */
    const exportReferences = async () => {
        if (!preview) return
        setExporting(true)
        setExportNote(null)
        try {
            const response = await api.post(`${BASE_URL}${EXPORT_PATH}`, { lenders: preview.lenders })
            if (!response.data?.success) throw new Error(response.data?.message || 'Export failed')
            const data = response.data as LenderPurgeExportResponse
            if (!data.rows.length) {
                setExportNote('No client or claim references to export for this selection.')
                return
            }
            const stamp = new Date().toISOString().slice(0, 10)
            downloadCsvFile(buildLenderPurgeExportCsv(data.rows), `fairclaim-lender-purge-references-${stamp}.csv`)
            setExportNote(
                data.truncated
                    ? `Downloaded first ${data.rows.length} of ${data.claims} claim rows — export was truncated at the server limit.`
                    : `Downloaded ${plural(data.claims, 'claim reference')} across ${plural(data.clients, 'client')}.`,
            )
        } catch (error: any) {
            Report.failure(
                'Export failed',
                error?.response?.data?.message || error?.message || 'Could not export the client and claim references.',
                'Okay',
            )
        } finally {
            setExporting(false)
        }
    }

    const runPurge = async () => {
        if (!preview || confirmText.toLowerCase() !== 'delete') return
        setPurging(true)
        try {
            const response = await api.post(`${BASE_URL}${PURGE_PATH}`, {
                lenders: preview.lenders,
                confirm: 'DELETE',
            })
            if (!response.data?.success) throw new Error(response.data?.message || 'Purge failed')
            const data = response.data as LenderPurgeResult
            setResult(data)
            setPreview(null)
            setConfirmText('')
            setSelected(new Set())
            Report.success('Clients purged', data.message || 'FairClaim records and S3 documents were deleted.', 'Okay')
            // Parent bumps refreshNonce, which reloads this card along with the stats and list.
            onPurged()
        } catch (error: any) {
            Report.failure(
                'Purge failed',
                error?.response?.data?.message || error?.message || 'Could not purge clients for these lenders.',
                'Okay',
            )
        } finally {
            setPurging(false)
        }
    }

    const busy = loading || previewLoading || purging

    return (
        <div>
            <p className={styles.footnote} style={{ marginTop: 0 }}>
                Every FairClaim client counted under each defendant they hold a claim with, split by whether the client
                has ever been placed in a funding batch. Lender spellings are grouped under the defendant name
                configured on the Approved Lenders page (funding-monitor defendants table: canonical name plus aliases);
                the spellings found on claims are listed under each name. Spellings that are not a configured name or
                alias are listed separately at the bottom for reference and cannot be purged from here. Audit-removed
                clients still count as in a batch. Purging a defendant deletes the <strong>whole client</strong> (all
                their claims, including those with other lenders) for clients <strong>not</strong> in a batch — the same
                scope as the CSV purge above. Clients in a batch are never touched.
            </p>

            <div className={styles.lenderSelectionBar}>
                <span>
                    {totals
                        ? `${plural(totals.lenders, 'lender')} · ${plural(totals.distinctClients, 'distinct client')} · ` +
                          `${totals.inBatch} in a batch · ${totals.notInBatch} not in a batch`
                        : loading
                          ? 'Loading lenders…'
                          : '—'}
                </span>
                <label
                    className={styles.testToggle}
                    title="Only list lenders with at least one client not in a funding batch"
                >
                    <input
                        type="checkbox"
                        checked={hideNonPurgeable}
                        onChange={(e) => setHideNonPurgeable(e.target.checked)}
                    />
                    Hide lenders with nothing to purge
                    {hideNonPurgeable && hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
                </label>
                <button
                    type="button"
                    className={styles.refreshButton}
                    onClick={() => setLocalNonce((n) => n + 1)}
                    disabled={busy}
                    title="Reload lender counts and funding-batch membership"
                >
                    <FaSyncAlt className={loading ? styles.refreshSpinning : undefined} aria-hidden="true" />
                    {loading ? 'Refreshing…' : 'Refresh'}
                </button>
            </div>

            {loadError ? (
                <p className={styles.errorText}>{loadError}</p>
            ) : (
                <div className={`${styles.lenderTableWrap} ${loading ? styles.tableLoading : ''}`}>
                    <table className={`${styles.dataTable} ${styles.lenderTable}`}>
                        <thead>
                            <tr>
                                <th className={styles.lenderCheckCol}>
                                    <input
                                        type="checkbox"
                                        aria-label="Select every lender with purgeable clients"
                                        checked={allPurgeableSelected}
                                        disabled={busy || purgeableRows.length === 0}
                                        onChange={toggleAll}
                                    />
                                </th>
                                <th className={styles.lenderNameCol}>Defendant</th>
                                <th className={styles.lenderNumCol}>Clients</th>
                                <th className={styles.lenderNumCol}>In funding batch</th>
                                <th className={styles.lenderNumCol}>Not in batch (purgeable)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className={styles.emptyCell}>
                                        {loading
                                            ? 'Loading…'
                                            : rows.length === 0
                                              ? 'No clients with claims found.'
                                              : matchedRows.length === 0
                                                ? 'No claims match a configured defendant — see the unmatched spellings below.'
                                                : 'Every defendant’s clients are in a funding batch — nothing to purge.'}
                                    </td>
                                </tr>
                            ) : (
                                visibleRows.map((row) => {
                                    const isSelected = selected.has(row.lender)
                                    const disabled = row.notInBatch === 0
                                    const rowClass = isSelected
                                        ? styles.lenderRowSelected
                                        : disabled
                                          ? styles.lenderRowDisabled
                                          : undefined
                                    return (
                                        <tr key={row.lender} className={rowClass}>
                                            <td className={styles.lenderCheckCol}>
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Select ${row.lender}`}
                                                    checked={isSelected}
                                                    disabled={busy || disabled}
                                                    onChange={() => toggleLender(row.lender)}
                                                    title={
                                                        disabled
                                                            ? 'Every client for this lender is in a funding batch'
                                                            : undefined
                                                    }
                                                />
                                            </td>
                                            <td className={styles.lenderNameCol}>
                                                <div>{row.lender}</div>
                                                {row.aliases.length > 0 && (
                                                    <div
                                                        className={styles.lenderAliases}
                                                        title={`Spellings on claims folded into ${row.lender}`}
                                                    >
                                                        Also stored as: {row.aliases.join(' · ')}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={styles.lenderNumCol}>{row.clients}</td>
                                            <td className={`${styles.lenderNumCol} ${styles.lenderPurgeGood}`}>
                                                {row.inBatch}
                                            </td>
                                            <td
                                                className={`${styles.lenderNumCol} ${
                                                    row.notInBatch > 0 ? styles.lenderPurgeBad : ''
                                                }`}
                                            >
                                                {row.notInBatch}
                                            </td>
                                        </tr>
                                    )
                                })
                            )}
                        </tbody>
                        {totals && rows.length > 0 && (
                            <tfoot>
                                <tr>
                                    <td className={styles.lenderCheckCol} />
                                    <td className={styles.lenderNameCol}>
                                        Distinct clients (all lenders, incl. unmatched spellings)
                                    </td>
                                    <td className={styles.lenderNumCol}>{totals.distinctClients}</td>
                                    <td className={styles.lenderNumCol}>{totals.inBatch}</td>
                                    <td className={styles.lenderNumCol}>{totals.notInBatch}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            )}
            <p className={styles.footnote} style={{ marginTop: 8 }}>
                Per-lender rows can overlap (a client with two lenders appears twice); the footer counts each client
                once.
            </p>

            <div className={styles.csvPurgeRow}>
                <span className={styles.footnote} style={{ margin: 0 }}>
                    {selectedList.length
                        ? `${plural(selectedList.length, 'lender')} selected · up to ${plural(
                              selectedNotInBatch,
                              'client',
                          )} not in a batch`
                        : 'Select one or more lenders to preview a purge.'}
                </span>
                <button
                    type="button"
                    className={styles.dangerButton}
                    disabled={!selectedList.length || busy}
                    onClick={runPreview}
                >
                    {previewLoading
                        ? 'Previewing…'
                        : `Preview purge${selectedList.length ? ` (${selectedList.length})` : ''}`}
                </button>
            </div>

            {preview && (
                <div className={styles.csvPurgePreview}>
                    <p>
                        Lenders: <strong>{preview.lenders.join(', ')}</strong>
                    </p>
                    <p>
                        Will delete <strong>{preview.matchedClients}</strong> client(s),{' '}
                        <strong>{preview.matchedLeads}</strong> lead(s), <strong>{preview.matchedDeliveries}</strong>{' '}
                        delivery record(s) and <strong>{preview.matchedRejections}</strong> rejection(s). S3 originals
                        and generated documents will be deleted ({preview.s3PrefixCount} prefix(es),{' '}
                        {preview.s3KeyCount} key(s)).
                    </p>
                    {preview.skippedInBatch > 0 && (
                        <p className={styles.csvPurgeWarning}>
                            {plural(preview.skippedInBatch, 'client')} for these lenders are in a funding batch (active
                            or audit-removed) and will be left in place.
                        </p>
                    )}
                    {preview.multiLenderClients > 0 && (
                        <p className={styles.csvPurgeWarning}>
                            {plural(preview.multiLenderClients, 'client')} also hold claims with lenders outside this
                            selection. The whole client is deleted, including those other claims.
                        </p>
                    )}
                    {preview.unknownLenders?.length > 0 && <p>Not found: {preview.unknownLenders.join(', ')}.</p>}
                    {preview.clients.length > 0 && (
                        <>
                            <p style={{ marginBottom: 0 }}>
                                Clients to be deleted
                                {preview.clientsTruncated
                                    ? ` (first ${preview.clients.length} of ${preview.matchedClients})`
                                    : ''}
                                :
                            </p>
                            <ul className={styles.lenderClientList}>
                                {preview.clients.map((c) => (
                                    <li key={c.id}>
                                        #{c.id} · {c.name || '—'} · {c.email || '—'}
                                        {c.clientRef ? ` · ${c.clientRef}` : ''}
                                        {c.lenders.length ? ` · ${c.lenders.join(', ')}` : ''}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {preview.matchedClients > 0 && (
                        <div className={styles.csvPurgeRow} style={{ marginTop: 10 }}>
                            <span className={styles.footnote} style={{ margin: 0 }}>
                                {exportNote ??
                                    'Export the client and claim references for the external claim provider before deleting.'}
                            </span>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                disabled={exporting || purging}
                                onClick={exportReferences}
                                title="CSV of every client ref, claim ref and agreement account id this purge would delete (read-only)"
                            >
                                {exporting ? 'Preparing…' : 'Export client & claim references (CSV)'}
                            </button>
                        </div>
                    )}
                    {preview.matchedClients > 0 ? (
                        <div className={styles.csvPurgeConfirm}>
                            <label>
                                Type <strong>delete</strong> to confirm
                                <input
                                    type="text"
                                    value={confirmText}
                                    onChange={(e) => setConfirmText(e.target.value)}
                                    autoComplete="off"
                                />
                            </label>
                            <button
                                type="button"
                                className={styles.dangerButton}
                                disabled={confirmText.toLowerCase() !== 'delete' || purging}
                                onClick={runPurge}
                            >
                                {purging ? 'Deleting…' : 'Delete clients and S3 documents'}
                            </button>
                        </div>
                    ) : (
                        <p>Nothing to purge — every client for the selected lender(s) is in a funding batch.</p>
                    )}
                </div>
            )}

            {result?.success && (
                <p className={styles.csvPurgeResult}>
                    {result.message} S3 objects deleted: {result.s3?.deleted ?? 0}
                    {result.s3?.failed?.length ? `; ${result.s3.failed.length} S3 delete(s) failed` : ''}.
                </p>
            )}

            {unmatchedRows.length > 0 && (
                <div className={styles.lenderUnmatchedSection}>
                    <CollapsibleSection
                        title="Lender spellings with no matching defendant"
                        meta={`${plural(unmatchedRows.length, 'spelling')} · ${plural(
                            unmatchedNotInBatch,
                            'client',
                        )} not in a batch · reference only`}
                    >
                        <p className={styles.footnote} style={{ marginTop: 0 }}>
                            These values appear on claims but are not a configured defendant name or alias on the
                            Approved Lenders page, so they cannot be selected above. To make one purgeable, add it as an
                            alias of the right defendant on the Approved Lenders page and refresh — it will then be
                            counted under that defendant.
                        </p>
                        <div className={`${styles.lenderTableWrap} ${loading ? styles.tableLoading : ''}`}>
                            <table className={`${styles.dataTable} ${styles.lenderTable}`}>
                                <thead>
                                    <tr>
                                        <th className={styles.lenderNameCol}>Lender as stored on claims</th>
                                        <th className={styles.lenderNumCol}>Clients</th>
                                        <th className={styles.lenderNumCol}>In funding batch</th>
                                        <th className={styles.lenderNumCol}>Not in batch</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {unmatchedRows.map((row) => (
                                        <tr key={row.lender}>
                                            <td className={styles.lenderNameCol}>{row.lender}</td>
                                            <td className={styles.lenderNumCol}>{row.clients}</td>
                                            <td className={`${styles.lenderNumCol} ${styles.lenderPurgeGood}`}>
                                                {row.inBatch}
                                            </td>
                                            <td className={styles.lenderNumCol}>{row.notInBatch}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CollapsibleSection>
                </div>
            )}
        </div>
    )
}
